import { encodeCanvas, extForBlob } from '../shared/imageOutputFormats';
import { toDisplayableBlob } from '../shared/heicDisplay';
import { drawPatternBackground } from './removeBackgroundPresets';
import api from '../../../../../services/api';

// HEIC is accepted too: `toDisplayableBlob` turns an iPhone photo into a JPEG
// before anything tries to draw it, so the model never sees a format the
// browser cannot decode.
const SUPPORTED_TYPES = new Set([
    'image/jpeg', 'image/png', 'image/webp', 'image/avif',
    'image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence',
]);
const SUPPORTED_EXT = /\.(jpe?g|png|webp|avif|heic|heif|hif)$/i;

export function isSupportedRemoveBgFile(file) {
    return SUPPORTED_TYPES.has(file.type) || SUPPORTED_EXT.test(file.name || '');
}

// What @imgly/background-removal itself accepts. It refuses anything else with
// "Incompatible image type", even formats the browser decodes happily (AVIF), so
// those are re-encoded as PNG before they reach the model.
const REMOVER_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

/** A blob the remover accepts: HEIC decoded, AVIF (and the rest) re-encoded as PNG. */
export async function toDrawableBlob(file) {
    const blob = await toDisplayableBlob(file);
    if (REMOVER_TYPES.has((blob.type || '').toLowerCase())) return blob;
    const img = await loadImageFromBlob(blob);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext('2d').drawImage(img, 0, 0);
    return canvasToPng(canvas);
}

function loadImageFromUrl(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });
}

// Hosted preset photos, fetched once per session and shared by every picture
// in a batch.
const remoteBlobCache = new Map();

/**
 * A hosted preset photo, as an image the canvas may export.
 *
 * Two routes, tried in order:
 *
 *  1. Straight from files.sohozkaj.com as a CORS blob. The `cors=1` query keeps
 *     it out of the cache entry the plain preset thumbnail filled — that
 *     response has no `Access-Control-Allow-Origin` and no `Vary: Origin`, so a
 *     shared entry would fail the CORS check.
 *  2. Through the API's image proxy (`/api/question-papers/image-proxy`, which
 *     allows files.sohozkaj.com). The file host only grants CORS to a short list
 *     of origins, so on any other one — 127.0.0.1, a LAN address, a preview
 *     domain — route 1 is refused and the background silently never appeared.
 *
 * Either way the result is a blob URL, which never taints the canvas.
 */
async function fetchRemoteBlob(url) {
    try {
        const sep = url.includes('?') ? '&' : '?';
        const res = await fetch(`${url}${sep}cors=1`, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) throw new Error(`preset ${res.status}`);
        return await res.blob();
    } catch {
        const blob = await api.get('/api/question-papers/image-proxy', {
            params: { url },
            responseType: 'blob',
        });
        if (!(blob instanceof Blob) || !blob.size) throw new Error('preset proxy failed');
        return blob;
    }
}

function loadRemoteImage(url) {
    if (!remoteBlobCache.has(url)) {
        const job = fetchRemoteBlob(url).catch((err) => {
            remoteBlobCache.delete(url); // let the next try fetch again
            throw err;
        });
        remoteBlobCache.set(url, job);
    }
    return remoteBlobCache.get(url).then(loadImageFromBlob);
}

function loadImageFromBlob(blob) {
    const url = URL.createObjectURL(blob);
    return loadImageFromUrl(url).finally(() => URL.revokeObjectURL(url));
}

async function loadImageFromFile(file) {
    // A HEIC background would paint nothing outside Safari, so it is decoded first.
    const blob = await toDisplayableBlob(file);
    const url = URL.createObjectURL(blob);
    return loadImageFromUrl(url).finally(() => URL.revokeObjectURL(url));
}

function drawImageCover(ctx, img, dx, dy, dw, dh) {
    const ir = img.naturalWidth / img.naturalHeight;
    const dr = dw / dh;
    let sx;
    let sy;
    let sw;
    let sh;
    if (ir > dr) {
        sh = img.naturalHeight;
        sw = img.naturalHeight * dr;
        sx = (img.naturalWidth - sw) / 2;
        sy = 0;
    } else {
        sw = img.naturalWidth;
        sh = img.naturalWidth / dr;
        sx = 0;
        sy = (img.naturalHeight - sh) / 2;
    }
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

function canvasToPng(canvas) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))), 'image/png');
    });
}

/**
 * Run AI background removal in a web worker.
 */
export function runBackgroundRemoval(imageUrl, onProgress) {
    return new Promise((resolve, reject) => {
        const worker = new Worker(new URL('./bgRemovalWorker.js', import.meta.url), { type: 'module' });
        worker.onmessage = (e) => {
            const { type, pct, blob, message } = e.data;
            if (type === 'progress') onProgress?.(pct);
            else if (type === 'done') {
                worker.terminate();
                resolve(blob);
            } else if (type === 'error') {
                worker.terminate();
                reject(new Error(message || 'failed'));
            }
        };
        worker.onerror = (err) => {
            worker.terminate();
            reject(err);
        };
        worker.postMessage({ imageUrl });
    });
}

/**
 * A background remover that keeps one worker alive across a batch.
 *
 * `runBackgroundRemoval` spawns and kills a worker per picture, which re-imports
 * the library and re-fetches the model every time. For a batch that cost lands
 * on every file, so the queue shares one worker and disposes it at the end.
 */
export function createBackgroundRemover() {
    let worker = null;
    let seq = 0;
    const jobs = new Map();

    const failAll = (err) => {
        jobs.forEach((job) => job.reject(err instanceof Error ? err : new Error('failed')));
        jobs.clear();
    };

    const ensureWorker = () => {
        if (worker) return worker;
        worker = new Worker(new URL('./bgRemovalWorker.js', import.meta.url), { type: 'module' });
        worker.onmessage = (e) => {
            const { type, pct, blob, message, jobId } = e.data;
            const job = jobs.get(jobId);
            if (!job) return;
            if (type === 'progress') {
                job.onProgress?.(pct);
            } else if (type === 'done') {
                jobs.delete(jobId);
                job.resolve(blob);
            } else if (type === 'error') {
                jobs.delete(jobId);
                job.reject(new Error(message || 'failed'));
            }
        };
        // A dead worker cannot be reused, so the next run gets a fresh one.
        worker.onerror = (err) => {
            worker?.terminate();
            worker = null;
            failAll(err);
        };
        return worker;
    };

    return {
        /** @param {Blob|string} source @param {(pct:number)=>void} [onProgress] */
        run(source, onProgress) {
            const jobId = seq + 1;
            seq = jobId;
            return new Promise((resolve, reject) => {
                jobs.set(jobId, { resolve, reject, onProgress });
                ensureWorker().postMessage({ imageUrl: source, jobId });
            });
        },
        dispose() {
            worker?.terminate();
            worker = null;
            failAll(new Error('disposed'));
        },
    };
}

/**
 * Composite a transparent cutout onto a solid color or photo background.
 */
export async function composeWithBackground(cutoutBlob, { bgMode, bgColor, bgPattern, bgImageFile, bgPhotoUrl, format = 'png' }) {
    // A transparent PNG is exactly what the model returned — nothing to redo.
    if (bgMode === 'transparent' && format === 'png') return cutoutBlob;

    const fgImg = await loadImageFromBlob(cutoutBlob);
    const canvas = document.createElement('canvas');
    canvas.width = fgImg.naturalWidth;
    canvas.height = fgImg.naturalHeight;
    const ctx = canvas.getContext('2d');

    if (bgMode === 'color' && bgPattern) {
        // A gradient or pattern preset from the colour tab wins over the solid.
        drawPatternBackground(ctx, canvas.width, canvas.height, bgPattern);
    } else if (bgMode === 'color') {
        ctx.fillStyle = bgColor || '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else if (bgMode === 'image' && bgImageFile) {
        const bgImg = await loadImageFromFile(bgImageFile);
        drawImageCover(ctx, bgImg, 0, 0, canvas.width, canvas.height);
    } else if (bgMode === 'image' && bgPhotoUrl) {
        const bgImg = await loadRemoteImage(bgPhotoUrl);
        drawImageCover(ctx, bgImg, 0, 0, canvas.width, canvas.height);
    }

    ctx.drawImage(fgImg, 0, 0);
    // JPG is laid onto white inside encodeCanvas; PNG and WebP keep the alpha.
    return (await encodeCanvas(canvas, format)).blob;
}

export function outputFileName(originalName, bgMode, blob) {
    const base = (originalName || 'image').replace(/\.[^.]+$/, '');
    const ext = extForBlob(blob, 'png');
    return bgMode === 'transparent' ? `${base}-no-bg.${ext}` : `${base}-new-bg.${ext}`;
}
